import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";
import axios from "axios";

const API_BASE = "https://crm.semicoloninnovations.in/api";

function SyllabusGenerator() {
  const [formData, setFormData] = useState({
    course_name: "",
    duration: "",
    course_type: "regular",
    days: [],
  });

  const [generatedText, setGeneratedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API_BASE}/syllabuses_get.php`);
      if (res.data.status === "success") {
        setHistory(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
    setLoadingHistory(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDayChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => {
      const days = checked
        ? [...prev.days, value]
        : prev.days.filter((d) => d !== value);
      return { ...prev, days };
    });
  };

  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedText("");
    setMessage("");
    try {
      const res = await axios.post(`${API_BASE}/syllabus_generate.php`, formData);
      if (res.data.status === "success") {
        setGeneratedText(res.data.syllabus);
      } else {
        setMessage(res.data.message || "Error generating syllabus");
      }
    } catch (err) {
      setMessage("Error connecting to API.");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!generatedText) {
      setMessage("Please generate a syllabus first.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_BASE}/syllabus_save.php`, {
        ...formData,
        syllabus_text: generatedText,
      });
      if (res.data.status === "success") {
        const id = res.data.id;
        window.open(`${API_BASE}/syllabus_pdf.php?id=${id}`, "_blank");
        loadHistory(); // Refresh list after saving
      } else {
        setMessage(res.data.message || "Error saving syllabus");
      }
    } catch (err) {
      setMessage("Error connecting to API.");
    }
    setSaving(false);
  };

  return (
    <div className="p-3">
      <Card className="shadow-sm p-4 mb-4">
        <h4 className="mb-3">AI Syllabus Generator</h4>
        <p className="text-muted">
          Automatically generate structured course syllabuses using AI.
        </p>

        {message && <Alert variant="danger">{message}</Alert>}

        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Course Name</Form.Label>
                <Form.Control
                  name="course_name"
                  value={formData.course_name}
                  onChange={handleChange}
                  placeholder="e.g. Artificial Intelligence"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Duration</Form.Label>
                <Form.Control
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="e.g. 1 Month"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Course Type</Form.Label>
                <Form.Select
                  name="course_type"
                  value={formData.course_type}
                  onChange={handleChange}
                >
                  <option value="regular">Regular (Mon–Fri)</option>
                  <option value="weekend">Weekend (Sat–Sun)</option>
                  <option value="custom">Custom Days</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {formData.course_type === "custom" && (
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Select Days</Form.Label>
                  <div className="d-flex flex-wrap">
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(
                      (day) => (
                        <Form.Check
                          inline
                          key={day}
                          type="checkbox"
                          label={day}
                          value={day}
                          checked={formData.days.includes(day)}
                          onChange={handleDayChange}
                          className="me-3"
                        />
                      )
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          )}

          <div className="text-end">
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" animation="border" /> Generating...
                </>
              ) : (
                "Generate Syllabus"
              )}
            </Button>
          </div>
        </Form>
      </Card>

      {generatedText && (
        <Card className="shadow-sm p-4 mb-4">
          <h5 className="mb-3">Generated Syllabus Preview</h5>
          <div
            className="border p-3 rounded"
            style={{
              background: "#f9f9f9",
              whiteSpace: "pre-line",
              fontFamily: "monospace",
              minHeight: "200px",
            }}
          >
            {generatedText}
          </div>

          <div className="text-end mt-3">
            <Button
              variant="success"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" animation="border" /> Saving...
                </>
              ) : (
                "Save & Download PDF"
              )}
            </Button>
          </div>
        </Card>
      )}

      <Card className="shadow-sm p-4">
        <h5 className="mb-3">Saved Syllabus History</h5>
        {loadingHistory ? (
          <Spinner animation="border" />
        ) : history.length === 0 ? (
          <p className="text-muted">No syllabuses generated yet.</p>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Course Name</th>
                <th>Duration</th>
                <th>Type</th>
                <th>Days</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {history.map((item, i) => (
                <tr key={item.id}>
                  <td>{i + 1}</td>
                  <td>{item.course_name}</td>
                  <td>{item.duration}</td>
                  <td>{item.course_type}</td>
                  <td>{item.days || "-"}</td>
                  <td>{new Date(item.created_at).toLocaleDateString()}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        window.open(`${API_BASE}/${item.pdf_url}`, "_blank")
                      }
                    >
                      View PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default SyllabusGenerator;
